import React from 'react';
import Grid from '@mui/material/Grid2';
import { useProjects } from '../api/projects';
import ProjectCard from './ProjectCard';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import { useNavigate } from 'react-router-dom';

interface ProjectsListingTiledProps {
  searchQuery: string;
  tagFilter?: string;
  forceRefresh?: boolean;
}

const AddProjectCard = () => {
  const navigate = useNavigate();

  const handleSingleProjectClick = () => {
    navigate('/create-project');
  };

  const handleBulkProjectClick = () => {
    navigate('/create-projects-bulk');
  };

  return (
    <Card sx={{ 
      width: 360, 
      height: 360, 
      position: 'relative', 
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      borderRadius: 2,
      '&:hover': {
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)',
      },
      transition: 'all 0.2s ease-in-out',
    }}>
      {/* Top-left diagonal section - Single Project */}
      <CardActionArea 
        onClick={handleSingleProjectClick}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          clipPath: 'polygon(0 0, 100% 0, 0 100%)',
          backgroundColor: '#f8f9fa',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transform: 'translate(-70%, -70%)',
          color: '#00d4ad',
          textAlign: 'center',
        }}>
          <Icon sx={{ fontSize: 48, mb: 1.5, color: '#00d4ad', fontWeight: 'bold' }}>add_circle</Icon>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.5px', color: '#00d4ad' }}>
            Create
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#00b496' }}>
            Single Project
          </Typography>
        </Box>
      </CardActionArea>

      {/* Bottom-right diagonal section - Bulk Upload */}
      <CardActionArea 
        onClick={handleBulkProjectClick}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
          backgroundColor: '#fff8e1',
          '&:hover': {
            backgroundColor: '#fff8e1',
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transform: 'translate(70%, 70%)',
          color: '#ff9800',
          textAlign: 'center',
        }}>
          <Icon sx={{ fontSize: 48, mb: 1.5, color: '#ff9800', fontWeight: 'bold' }}>cloud_upload</Icon>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.5px', color: '#ff9800' }}>
            Upload
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#f57c00' }}>
            Bulk CSV
          </Typography>
        </Box>
      </CardActionArea>

      {/* Subtle diagonal separator */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '141.42%',
          height: '1px',
          background: 'linear-gradient(45deg, transparent 0%, rgba(128,128,128,0.3) 50%, transparent 100%)',
          transformOrigin: 'top left',
          transform: 'rotate(-45deg)',
        },
      }} />
    </Card>
  );
};

const ProjectsListingTiled: React.FC<ProjectsListingTiledProps> = ({ searchQuery, tagFilter }) => {
  const { projects, isLoading, error } = useProjects(searchQuery, tagFilter);
  const { user } = useAuth();
  const hasCreateProjectCapability = user?.capabilities?.some(
    (capability) => capability.codename === 'create_project'
  );
  const canCreateProject =
    !isViewOnly(user) && (user?.is_staff || hasCreateProjectCapability);

  if (isLoading) {
    return <div>Loading projects...</div>;
  }

  if (error) {
    return <div>Error loading projects: {error.message}</div>;
  }

  if (!projects?.data) {
    return <div>No projects found.</div>
  }

  return (
    <Grid container
          spacing={2}
          sx={{ 
            width: '100%',
            '&.MuiGrid2-root': { 
              display: 'flex', 
              alignContent: "flex-start",
              justifyContent: 'center',
            },
          }}>
      {canCreateProject && <Grid key={'add-project'}>
        <AddProjectCard/>
      </Grid>}
      {projects.data.map((project) => (
        <Grid key={project.id}>
          <ProjectCard
            project={project}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default ProjectsListingTiled;